import * as THREE from 'three';
import { InsideType, ModuleType } from '../../../../constants/enum';
import { message } from 'antd';
import axios from 'axios';
import { nearlyEqual, roundTo } from '../../../../utils/geometry/epsilon';
import { GeometryUtils } from '../../../../utils/geometry/GeometryUtils';
import { openMessageError } from '../../../../components/common/Message/openMessageError';
import { openMessageWarning } from '../../../../components/common/Message/openMessageWarning';
import i18n from '../../../../languages/i18n';
// import * as Sentry from '@sentry/react';

export default class TextureModuleController {
  constructor() {
    this.textureLoader = new THREE.TextureLoader();
    this.textureCache = new Map();
    this.modules = new Map();
    this.FixedItem = [
      InsideType.CLOTHES_HANGER,
      InsideType.LED_RECESSED,
      InsideType.LED_CORNER,
      InsideType.LOCK,
      InsideType.HANDLE,
      InsideType.ACCESSORY_GENERAL,
      InsideType.ACCESSORY_DISH_UPPER,
      InsideType.ACCESSORY_DISH_LOWER,
      InsideType.ACCESSORY_SINK, //Bồn rửa
      InsideType.ACCESSORY_FRIDGE, //tủ lạnh
      InsideType.ACCESSORY_COOKER, //bếp từ
      InsideType.ACCESSORY_HOOD,
      InsideType.ACCESSORY_FAUCET,
      InsideType.ACCESSORY_WASHER,
      InsideType.KHUNG_SAT,
      InsideType.CHAN_ROI_TV,
    ];
  }

  /**
   * Trích xuất tất cả material từ một module (không phụ thuộc vào texture có sẵn)
   */
  extractMaterials(module) {
    const materials = new Map();

    module.traverse(child => {
      if (child.material) {
        const materialArray = Array.isArray(child.material)
          ? child.material
          : [child.material];

        materialArray.forEach((material, index) => {
          const materialKey = `${child.uuid}_${index}`;

          // Lưu material và thông tin gốc
          materials.set(materialKey, {
            material: material,
            meshType: child.userData.name,
            child: child,
            originalProperties: {
              map: material.map || null,
              normalMap: material.normalMap || null,
              roughnessMap: material.roughnessMap || null,
              metalnessMap: material.metalnessMap || null,
              aoMap: material.aoMap || null,
              emissiveMap: material.emissiveMap || null,
              bumpMap: material.bumpMap || null,
              displacementMap: material.displacementMap || null,
              color: material.color ? material.color : null,
            },
          });
        });
      }
    });

    return materials;
  }

  async loadTexture(url) {
    if (this.textureCache.has(url)) {
      return this.textureCache.get(url);
    }

    // Cache the in-flight promise immediately so concurrent callers for the
    // same url (e.g. multiple modules sharing one material loading in
    // parallel) await the same request instead of each firing a fetch.
    const loadPromise = this._fetchTexture(url);
    this.textureCache.set(url, loadPromise);
    try {
      const texture = await loadPromise;
      this.textureCache.set(url, texture);
      return texture;
    } catch (error) {
      this.textureCache.delete(url);
      throw error;
    }
  }

  async _fetchTexture(url) {
    const startTime = performance.now();
    // const logData = {
    //   url,
    //   userAgent: navigator.userAgent,
    //   isOnline: navigator.onLine,
    //   timestamp: new Date().toISOString(),
    //   expectedSize: null,
    //   actualSize: null,
    //   status: null,
    //   error: null,
    // };

    try {
      const response = await axios({
        method: 'get',
        url,
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      // logData.status = response.status;
      // logData.expectedSize = response.headers['content-length'];
      // logData.actualSize = response.data.size;
      // logData.contentType = response.headers['content-type'];
      // logData.transferEncoding = response.headers['transfer-encoding'];
      // logData.contentEncoding = response.headers['content-encoding'];

      // // Log dữ liệu request
      // console.table(logData);

      // // Kiểm tra nếu kích thước không khớp
      // if (
      //   logData.expectedSize &&
      //   logData.actualSize &&
      //   parseInt(logData.expectedSize) !== logData.actualSize
      // ) {
      //   console.warn(
      //     `⚠️ Size mismatch: expected ${logData.expectedSize}, got ${logData.actualSize}`
      //   );
      // }

      // Tạo object URL từ blob
      const imageUrl = URL.createObjectURL(response.data);

      // Load texture từ object URL
      return new Promise((resolve, reject) => {
        this.textureLoader.load(
          imageUrl,
          texture => {
            URL.revokeObjectURL(imageUrl); // Giải phóng memory
            resolve(texture);
          },
          undefined,
          error => {
            URL.revokeObjectURL(imageUrl);
            // logData.error = error.message || error;
            // logData.duration = performance.now() - startTime;
            // console.error('✗ Texture loading failed:', logData);
            // Sentry.captureException('✗ Axios request failed:', logData);
            reject(error);
          }
        );
      });
    } catch (error) {
      // logData.error = error.message || error;
      // logData.duration = performance.now() - startTime;
      // console.error('✗ Axios request failed:', logData);
      // Sentry.captureException('✗ Axios request failed:', logData);
      throw error;
    }
  }

  async reloadTexture({
    module,
    mapExcludeMeshes = [
      'MAT-DA',
      'VACH-BEP',
      'RAY',
      'MOC-TREO',
      'GLASS-PANEL',
      'VACH-LAM',
      'VACH-TRON',
      'VACH-GUONG',
      'GUONG',
      'CHAN-BTD',
      'NEM-NGOI',
      'VAT-GIUONG',
    ],
    mapIncludeMeshes = [],
    mapExcludeMeshTypes = [InsideType.DOOR_TEMPLATE],
    mapIncludeMeshTypes = [],
    colorExcludeMeshes = [],
    colorIncludeMeshes = [],
    colorExcludeMeshTypes = [InsideType.DOOR_TEMPLATE],
    colorIncludeMeshTypes = [InsideType.DOOR],
  }) {
    if (module.textures?.size) {
      for (const [
        key,
        {
          textureId,
          supplier,
          textureCode,
          texturePrice,
          material,
          textureUrl,
          paintColor,
          no_grain = false,
          specialTexture: special,
        },
      ] of module.textures) {
        if (
          textureId &&
          // supplier &&
          textureCode &&
          texturePrice &&
          textureUrl
        ) {
          if (key === 'Back') {
            await this.changeTextureHau({
              module,
              texture: {
                url: textureUrl,
                textureId,
                supplier,
                textureCode,
                texturePrice,
                material,
                no_grain,
                special,
              },
            });
          } else if (
            [
              'frame',
              'DOOR',
              'VACH-GUONG',
              'VACH-TRON',
              'VACH-LAM',
              'vat_giuong',
            ].includes(key)
          ) {
            if (key === 'DOOR' && material === 'Aluminum') {
              await this.changeTextureAlumDoor({
                module,
                texture: {
                  url: textureUrl,
                  textureId,
                  textureCode,
                  texturePrice,
                  material,
                },
              });
            } else if (key === 'vat_giuong') {
              await this.changeTexture({
                module,
                texture: {
                  url: textureUrl,
                  textureId,
                  supplier,
                  textureCode,
                  texturePrice,
                  material,
                  no_grain,
                  special,
                },
                includeMeshes: ['VAT-GIUONG'],
              });
            } else {
              await this.changeTexture({
                module,
                texture: {
                  url: textureUrl,
                  textureId,
                  supplier,
                  textureCode,
                  texturePrice,
                  material,
                  no_grain,
                  special,
                },
                excludeMeshes: [
                  ...(key === 'frame' ? [...mapExcludeMeshes] : []),
                ],
                includeMeshes: [
                  ...mapIncludeMeshes,
                  ...(key === 'frame' ? [] : [key]),
                ],
                excludeMeshTypes: [
                  ...mapExcludeMeshTypes,
                  ...(key === 'frame' ? [InsideType.DOOR] : []),
                ],
                includeMeshTypes: [
                  ...mapIncludeMeshTypes,
                  // ...(key === "frame" ? [] : [key]),
                ],
              });
            }
          } else if (['DOOR-ALUM'].includes(key)) {
            await this.changeTextureAlumDoor({
              module,
              texture: {
                url: textureUrl,
                textureId,
                textureCode,
                texturePrice,
                material,
              },
            });
          }
        }
        if (['shoe_lower_bench'].includes(key)) {
          await this.changeShoeLowerBench({
            module,
            texture: {
              url: textureUrl,
              textureId,
              supplier,
              textureCode,
              texturePrice,
              material,
            },
          });
        }
        if (['GLASS-PANEL'].includes(key)) {
          await this.changeGlass({
            module,
            texture: {
              url: textureUrl,
              textureId,
              textureCode,
              texturePrice,
            },

            // includeMeshTypes: [
            //   InsideType.DOOR,
            //   // ...(key === "frame" ? [] : [key]),
            // ],
          });
        }
        if (paintColor) {
          this.changeTexture({
            module,
            texture: {
              special,
            },
            textureType: 'color',
            color: paintColor,
            excludeMeshes: [...(key === 'frame' ? colorExcludeMeshes : [])],
            includeMeshes: [
              ...colorIncludeMeshes,
              ...(key === 'frame' ? [] : [key]),
            ],
            excludeMeshTypes: [
              ...colorExcludeMeshTypes,
              ...(key === 'frame' ? [InsideType.DOOR] : []),
            ],
            includeMeshTypes: [
              ...colorIncludeMeshTypes,
              // ...(key === "frame" ? [] : [key]),
            ],
          });
        }
      }
    }
  }

  /**
   * Tính toán scale factor giữa geometry hiện tại và geometry gốc
   * @param {THREE.BufferGeometry} currentGeometry - Geometry hiện tại (đã scale)
   * @param {THREE.BufferGeometry} originalGeometry - Geometry gốc (scale 1,1,1)
   * @returns {Object} {scaleX, scaleY, scaleZ}
   */
  calculateScaleFactor(currentGeometry, originalGeometry) {
    currentGeometry.computeBoundingBox();
    originalGeometry.computeBoundingBox();

    const currentBox = currentGeometry.boundingBox;
    const originalBox = originalGeometry.boundingBox;

    const currentSizeX = currentBox.max.x - currentBox.min.x;
    const currentSizeY = currentBox.max.y - currentBox.min.y;
    const currentSizeZ = currentBox.max.z - currentBox.min.z;

    const originalSizeX = originalBox.max.x - originalBox.min.x;
    const originalSizeY = originalBox.max.y - originalBox.min.y;
    const originalSizeZ = originalBox.max.z - originalBox.min.z;

    return {
      scaleX:
        originalSizeX !== 0 && currentSizeX !== 0
          ? currentSizeX / originalSizeX
          : 1,
      scaleY:
        originalSizeY !== 0 && currentSizeY !== 0
          ? currentSizeY / originalSizeY
          : 1,
      scaleZ:
        originalSizeZ !== 0 && currentSizeZ !== 0
          ? currentSizeZ / originalSizeZ
          : 1,
    };
  }

  /**
   * Tạo UV dựa trên tỉ lệ scale của từng trục
   * @param {THREE.BufferGeometry} geometry - Geometry hiện tại
   * @param {number} textureScale - Base texture scale
   * @param {Object} scaleFactor - {scaleX, scaleY, scaleZ}
   */

  generateScaledUV(
    geometry,
    textureScale = 1,
    scaleFactor = { scaleX: 1, scaleY: 1, scaleZ: 1 }
  ) {
    if (!geometry.attributes.position || !geometry.attributes.normal) {
      console.warn('Geometry cần có position và normal attributes');
      if (!geometry.attributes.normal) {
        geometry.computeVertexNormals();
      }
      return;
    }

    geometry.computeBoundingBox();
    const { min, max } = geometry.boundingBox;

    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal;
    const uvs = [];

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i);
      const y = positions.getY(i);
      const z = positions.getZ(i);

      const nx = normals.getX(i);
      const ny = normals.getY(i);
      const nz = normals.getZ(i);

      let u, v;

      const absNx = Math.abs(nx);
      const absNy = Math.abs(ny);
      const absNz = Math.abs(nz);

      if (absNx > absNy && absNx > absNz) {
        // Mặt trái/phải (X-axis) - sử dụng scaleZ và scaleY
        u = (z - min.z) / (textureScale * (1 / scaleFactor.scaleZ));
        v = (y - min.y) / (textureScale * (1 / scaleFactor.scaleY));
      } else if (absNy > absNx && absNy > absNz) {
        // Mặt trên/dưới (Y-axis) - sử dụng scaleX và scaleZ
        u = (x - min.x) / (textureScale * (1 / scaleFactor.scaleX));
        v = (z - min.z) / (textureScale * (1 / scaleFactor.scaleZ));
      } else {
        // Mặt trước/sau (Z-axis) - sử dụng scaleX và scaleY
        u = (x - min.x) / (textureScale * (1 / scaleFactor.scaleX));
        v = (y - min.y) / (textureScale * (1 / scaleFactor.scaleY));
      }

      uvs.push(u, v);
    }

    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.attributes.uv.needsUpdate = true;
  }

  /**
   * FIXED: Apply texture với exclude meshes và fix material sharing
   */
  async changeTexture({
    module,
    textureType = 'map',
    texture: {
      url,
      textureId,
      material,
      supplier,
      textureCode,
      texturePrice,
      no_grain = false,
      special = false,
    },
    color,
    options = {},
    excludeMeshes = [
      'MAT-DA',
      'VACH-BEP',
      'RAY',
      'MOC-TREO',
      'GLASS-PANEL',
      'GUONG',
      'NEM-NGOI',
      'KINH-THUY',
      'CHAN-BTD',
      'LAM_BASE',
      'LAM_SLAT',
    ],
    includeMeshes = [],
    excludeMeshTypes = [],
    includeMeshTypes = [],
  }) {
    //Mặc định thêm kính cửa tủ vào danh sách loại trừ
    // excludeMeshes = ['MAT-DA', 'VACH-BEP', 'RAY', 'GLASS-PANEL', 'VACH-LAM'];

    let materialType = null;
    let currMaterial = null;
    const group = module.group;
    group.traverse(child => {
      if (
        child.isGroup &&
        child.userData.type === InsideType.DOOR &&
        child.userData.isInside &&
        includeMeshTypes.includes(InsideType.DOOR)
      ) {
        materialType = child.userData.materialType;
        currMaterial = child.children?.find(
          c => c.userData.type === InsideType.DOOR
        )?.userData?.material;
      }
    });
    if (group?.userData?.moduleType?.includes('present')) return;
    if (materialType) {
      if (
        // material === 'Plywood' ||
        material === 'Plastic' &&
        ['la_sach', 'pano'].includes(materialType)
      ) {
        openMessageWarning({
          content: i18n.t('Plastic materials can not applyied to this doors'),
        });
        return;
      }
      if (
        material === 'Plywood' &&
        //  || material === 'Plastic'
        ['la_sach'].includes(materialType)
      ) {
        openMessageWarning({
          content: i18n.t('Plywood materials can not apply to this doors'),
        });
        return;
      }
      // if (color && currMaterial === 'Plastic') {
      //   openMessageWarning({
      //     content: i18n.t('2K paint cannot be applied to Plastic materials'),
      //   });
      //   return;
      // }
      // if (
      //   color &&
      //   !['tron', 'kinh'].includes(materialType) &&
      //   currMaterial === 'Plywood'
      // ) {
      //   openMessageWarning({
      //     content: i18n.t(
      //       '2K paint can only be applied to Plywood materials on flush doors  and glass doors'
      //     ),
      //   });
      //   return;
      // }
      if (color && !currMaterial) {
        openMessageWarning({
          content: i18n.t('2K paint cannot be applied to unknown materials'),
        });
        return;
      }
    }

    try {
      let newTexture;

      if (textureType === 'color') {
        newTexture = new THREE.Color(color);
      } else {
        try {
          newTexture = await this.loadTexture(url);
          // this.applyTextureOptions(newTexture, { ...options, rotation: 0 });
        } catch (error) {
          openMessageError({
            content: i18n.t('Failed to load materials. Please try again'),
          });
          return;
        }
      }

      // 🔧 BƯỚC 1: Clone material cho excluded meshes để tránh sharing
      // await this.isolateExcludedMeshMaterials(group, [...excludeMeshes, 'HAU']);

      // const processedMaterials = new Set();

      const setTexture = child => {
        const meshName = child.userData?.name || child.name || '';
        const type = child.userData.type ?? child?.parent?.userData?.type;

        const shouldInclude =
          includeMeshes.some(
            includeName =>
              meshName.includes(includeName) || meshName === includeName
          ) || includeMeshTypes.includes(type);

        const shouldExclude =
          excludeMeshes.some(
            excludeName =>
              meshName.includes(excludeName) || meshName === excludeName
          ) ||
          this.FixedItem.some(item => item === type) ||
          excludeMeshTypes.includes(type);
        if (shouldExclude && !shouldInclude) {
          return;
        }

        // Kiểm tra nếu loại vật liệu là acrylic nhưng không được cấp quyền sử dụng Acrylic thì return
        if (
          supplier?.materialType.toLowerCase().includes('acrylic') &&
          !child.userData?.useAcrylic
        ) {
          return;
        }
        if (child.material) {
          // Ensure material properties are valid before cloning

          if (!child.material.color || !color) {
            child.material.color = new THREE.Color();
          }
          child.material = child.material.clone();

          // } else {
          //   console.warn(
          //     `Material color is null or invalid for mesh: ${child.name}`
          //   );
          //   return; // Skip processing this child
          // }

          const cloneTexture = newTexture?.clone();

          ///// START
          // MODEL 1: Giữ nguyên scale hiện tại
          const modelWithScale = child.clone();
          modelWithScale.position.set(0, 0, 0);
          modelWithScale.rotation.set(0, 0, 0);
          modelWithScale.updateMatrix();
          modelWithScale.updateMatrixWorld(true);

          // MODEL 2: Reset scale về 1,1,1
          const modelOriginal = child.clone();
          modelOriginal.position.set(0, 0, 0);
          modelOriginal.rotation.set(0, 0, 0);
          modelOriginal.scale.set(1, 1, 1);
          modelOriginal.updateMatrix();
          modelOriginal.updateMatrixWorld(true);

          // Apply matrix vào geometry để có kích thước thực
          const geometryWithScale = modelWithScale.geometry.clone();
          geometryWithScale.applyMatrix4(modelWithScale.matrix);
          geometryWithScale.computeBoundingBox();

          const geometryOriginal = modelOriginal.geometry.clone();
          geometryOriginal.applyMatrix4(modelOriginal.matrix);
          geometryOriginal.computeBoundingBox();

          // Tính scale factor
          const scaleFactor = this.calculateScaleFactor(
            geometryWithScale,
            geometryOriginal
          );
          ///// END

          if (
            shouldInclude ||
            (includeMeshes.length === 0 &&
              includeMeshTypes.length === 0 &&
              !['HAU'].some(item => meshName.includes(item)))
          ) {
            // Áp dụng UV với scale factor
            if (!['MAIN'].some(item => meshName.includes(item))) {
              this.generateScaledUV(child.geometry, 0.999, scaleFactor);
            }
            // this.generateScaledUV(child.geometry, 0.999, scaleFactor);

            if (cloneTexture instanceof THREE.Texture)
              this.applyTextureOptions(cloneTexture, { ...options });

            //Xoay chiều vân cửa hộc kéo
            if (
              type === InsideType.DOOR &&
              child.parent?.userData.drawerUuid &&
              textureType === 'map'
            ) {
              if (cloneTexture instanceof THREE.Texture)
                this.applyTextureOptions(cloneTexture, {
                  rotation: Math.PI,
                  center: [0.5, 0.5],
                });
            }

            if (
              [
                'CHAN',
                'XUONG',
                'NEP',
                'BRACE',
                'MAIN',
                'MAT_TRUOC',
                'MAT_SAU',
                'MAT_TRAI',
                'MAT_PHAI',
                'VAN-NGANG',
                'THANH-GO-TREO',
              ].some(item => meshName.includes(item))
            ) {
              this.applyTextureOptions(cloneTexture ?? {}, { rotation: 0 });
            }

            //Xoay vân cho 1 số mesh bên trái của tủ góc
            if (module.moduleType?.includes('corner')) {
              if (
                ['DAY-1', 'NOC-1', 'XAN-NGANG-1'].some(item =>
                  meshName.includes(item)
                )
              ) {
                this.applyTextureOptions(cloneTexture ?? {}, { rotation: 0 });
              }
            }

            //Giường ngủ để vân xoay theo mặc định
            if (module.moduleType?.includes('bed')) {
              this.applyTextureOptions(cloneTexture ?? {}, { rotation: 0 });

              if (
                ['VAT-NGANG', 'NEP-NGANG'].some(item => meshName.includes(item))
              ) {
                this.applyTextureOptions(cloneTexture ?? {}, {
                  rotation: Math.PI / 2,
                });
              }
            }

            if (textureType === 'color') {
              // //Xoay chiều vân xấn dọc
              // if (child.userData.type === InsideType.VPANEL) {
              //   cloneTexture.rotation = Math.PI;
              //   cloneTexture.center.set(0, 0);
              //   cloneTexture.flipY = false;
              //   cloneTexture.repeat.set(0.5, 1);
              // }

              // Apply texture
              child.material.map = undefined;
            }
            child.material[textureType] = cloneTexture;
            child.material.name = url;
            child.material.needsUpdate = true;

            if (supplier?.materialType.toLowerCase().includes('acrylic')) {
              child.material.roughness = 0.275;
            } else {
              child.material.roughness = 1;
            }

            let oldTexture;
            if (shouldInclude) {
              let key =
                includeMeshes.find(
                  item => meshName.includes(item) || meshName === item
                ) ?? includeMeshTypes.find(item => item === type);
              if (key === InsideType.DOOR || key === 'DOOR-PANO') key = 'DOOR';
              if (meshName.includes('VAT-GIUONG')) key = 'vat_giuong';
              oldTexture = module.textures.get(key);
              module.textures.set(key, {
                textureId: textureId ?? oldTexture?.textureId,
                supplier: supplier ?? oldTexture?.supplier,
                material: material ?? oldTexture?.material,
                textureCode: textureCode ?? oldTexture?.textureCode,
                texturePrice: texturePrice ?? oldTexture?.texturePrice,
                textureUrl: url ?? oldTexture?.textureUrl,
                paintColor: color,
                no_grain:
                  textureType === 'color' ? oldTexture?.no_grain : no_grain,
                specialTexture:
                  textureType === 'color' ? oldTexture.specialTexture : special,
              });
            } else {
              oldTexture = module.textures.get('frame');
              module.textures.set('frame', {
                textureId: textureId ?? oldTexture?.textureId,
                supplier: supplier ?? oldTexture?.supplier,
                material: material ?? oldTexture?.material,
                textureCode: textureCode ?? oldTexture?.textureCode,
                texturePrice: texturePrice ?? oldTexture?.texturePrice,
                textureUrl: url ?? oldTexture?.textureUrl,
                paintColor: color,
                no_grain:
                  textureType === 'color' ? oldTexture?.no_grain : no_grain,
                specialTexture:
                  textureType === 'color' ? oldTexture.specialTexture : special,
              });
            }

            child.userData = {
              ...child.userData,
              ...(textureType === 'map'
                ? {
                    textureId,
                    supplier,
                    material,
                    textureCode,
                    texturePrice,
                    textureUrl: url,
                    no_grain:
                      textureType === 'color' ? oldTexture?.no_grain : no_grain,
                    specialTexture:
                      textureType === 'color'
                        ? oldTexture.specialTexture
                        : special,
                  }
                : {}),

              paintColor: color,
            };

            const parent = child.parent;

            if ([11].includes(parent.userData.type)) {
              parent.userData = {
                ...parent.userData,
                ...(textureType === 'map'
                  ? {
                      textureId,
                      supplier,
                      material,
                      textureCode,
                      texturePrice,
                      textureUrl: url,
                      no_grain:
                        textureType === 'color'
                          ? oldTexture?.no_grain
                          : no_grain,
                      specialTexture:
                        textureType === 'color'
                          ? oldTexture.specialTexture
                          : special,
                    }
                  : {}),
              };
            }
          }
        }
      };

      group.children.forEach(group => {
        // kiểm tra phần tử này nếu là cửa thì thêm vào danh sách
        if (
          group.isGroup &&
          group.userData.type === InsideType.DOOR &&
          group.userData.isInside &&
          group.userData.materialType !== 'nhom_kinh'
        ) {
          // this.doors.push(group);
          group?.traverse(child => {
            // if (!excludeMeshes.some(item => child.name.includes(item))) {
            setTexture(child);
            // }
          });
        }
        // group này chứa mesh chính của module
        if (
          group.isGroup &&
          ['Scene', 'VACH-LAM-GENERATED'].includes(group.name)
        ) {
          // Phụ kiện không duyệt sâu
          if (
            !this.FixedItem.some(item => item === group.userData.type) &&
            ![
              InsideType.KHUNG_SAT,
              InsideType.CHAN_ROI_TV,
              InsideType.DOOR,
            ].includes(group.userData.type)
          ) {
            group?.traverse(child => {
              // if (
              //   !child.userData?.isHelperBox &&
              //   !excludeMeshes.some(item => child.name.includes(item))
              // ) {
              setTexture(child);
              // }
            });
          }
        }
        // group này chứa xắn dọc gần đỉnh của module
        else if (
          group.isGroup &&
          [InsideType.VPANEL2, InsideType.HPANEL_SHOE].includes(
            group.userData.type
          )
        ) {
          group?.traverse(child => {
            setTexture(child);
          });
        }
        // phần còn lại là các xắn dọc, xắn ngang, bản lề, bạt tủ,
        else if (group.isMesh) {
          const mesh = group;
          if (
            ['MAIN-W-', 'MAIN-HP-', 'BRACE', 'HPANEL-'].some(item =>
              mesh.name.includes(item)
            ) ||
            [
              InsideType.BRACE,
              InsideType.VPANEL,
              InsideType.HPANEL,
              InsideType.HPANEL_LEFT,
              InsideType.HPANEL_RIGHT,

              // InsideType.DOOR,
            ].some(item => mesh.userData.type === item)
          ) {
            setTexture(mesh);
          }
        }
      });

      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  /**
   * FIXED: Apply texture với exclude meshes và fix material sharing
   */
  async changeTextureAlumDoor({
    module,
    textureType = 'map',
    texture: { url, textureId, material, textureCode, texturePrice },
    options = {},
    excludeMeshes = ['GLASS-PANEL'],
    // includeMeshes = [],
    excludeMeshTypes = [],
    // includeMeshTypes = [],
  }) {
    //Mặc định thêm kính cửa tủ vào danh sách loại trừ
    // excludeMeshes = ['MAT-DA', 'VACH-BEP', 'RAY', 'GLASS-PANEL', 'VACH-LAM'];
    const group = module.group;

    try {
      let newTexture;

      if (textureType === 'color') {
        newTexture = new THREE.Color(color);
      } else {
        try {
          newTexture = await this.loadTexture(url);
          // this.applyTextureOptions(newTexture, { ...options, rotation: 0 });
        } catch (error) {
          openMessageError({
            content: i18n.t('Failed to load materials. Please try again'),
          });
          return;
        }
      }

      // 🔧 BƯỚC 1: Clone material cho excluded meshes để tránh sharing
      // await this.isolateExcludedMeshMaterials(group, [...excludeMeshes, 'HAU']);

      // const processedMaterials = new Set();

      const setTexture = child => {
        const meshName = child.userData?.name || child.name || '';
        const type = child.userData.type ?? child?.parent?.userData?.type;

        // const shouldInclude =
        //   includeMeshes.some(
        //     includeName =>
        //       meshName.includes(includeName) || meshName === includeName
        //   ) || includeMeshTypes.includes(type);

        const shouldExclude =
          excludeMeshes.some(
            excludeName =>
              meshName.includes(excludeName) || meshName === excludeName
          ) ||
          this.FixedItem.some(item => item === type) ||
          excludeMeshTypes.includes(type);
        if (shouldExclude) {
          return;
        }
        if (child.material) {
          // Ensure material properties are valid before cloning

          if (!child.material.color) {
            child.material.color = new THREE.Color();
          }
          child.material = child.material.clone();

          // } else {
          //   console.warn(
          //     `Material color is null or invalid for mesh: ${child.name}`
          //   );
          //   return; // Skip processing this child
          // }

          const cloneTexture = newTexture?.clone();

          ///// START
          // MODEL 1: Giữ nguyên scale hiện tại
          const modelWithScale = child.clone();
          modelWithScale.position.set(0, 0, 0);
          modelWithScale.rotation.set(0, 0, 0);
          modelWithScale.updateMatrix();
          modelWithScale.updateMatrixWorld(true);

          // MODEL 2: Reset scale về 1,1,1
          const modelOriginal = child.clone();
          modelOriginal.position.set(0, 0, 0);
          modelOriginal.rotation.set(0, 0, 0);
          modelOriginal.scale.set(1, 1, 1);
          modelOriginal.updateMatrix();
          modelOriginal.updateMatrixWorld(true);

          // Apply matrix vào geometry để có kích thước thực
          const geometryWithScale = modelWithScale.geometry.clone();
          geometryWithScale.applyMatrix4(modelWithScale.matrix);
          geometryWithScale.computeBoundingBox();

          const geometryOriginal = modelOriginal.geometry.clone();
          geometryOriginal.applyMatrix4(modelOriginal.matrix);
          geometryOriginal.computeBoundingBox();

          // Tính scale factor
          const scaleFactor = this.calculateScaleFactor(
            geometryWithScale,
            geometryOriginal
          );
          ///// END

          // Áp dụng UV với scale factor
          if (!['MAIN'].some(item => meshName.includes(item))) {
            this.generateScaledUV(child.geometry, 0.999, scaleFactor);
          }
          // this.generateScaledUV(child.geometry, 0.999, scaleFactor);

          if (cloneTexture instanceof THREE.Texture)
            this.applyTextureOptions(cloneTexture, { ...options });

          //Xoay chiều vân cửa hộc kéo
          if (
            type === InsideType.DOOR &&
            child.parent?.userData.drawerUuid &&
            textureType === 'map'
          ) {
            if (cloneTexture instanceof THREE.Texture)
              this.applyTextureOptions(cloneTexture, {
                rotation: Math.PI,
                center: [0.5, 0.5],
              });
          }

          child.material[textureType] = cloneTexture;
          child.material.name = url;
          child.material.needsUpdate = true;

          let oldTexture;
          // if (shouldInclude) {
          let key = 'DOOR-ALUM';
          // if (key === InsideType.DOOR || key === 'DOOR-PANO') key = 'DOOR';
          oldTexture = module.textures.get(key);
          module.textures.set(key, {
            textureId: textureId ?? oldTexture?.textureId,
            material: material ?? oldTexture?.material,
            textureCode: textureCode ?? oldTexture?.textureCode,
            texturePrice: texturePrice ?? oldTexture?.texturePrice,
            textureUrl: url ?? oldTexture?.textureUrl,
          });
          // } else {
          //   oldTexture = module.textures.get('frame');
          //   module.textures.set('frame', {
          //     textureId: textureId ?? oldTexture?.textureId,
          //     material: material ?? oldTexture?.material,
          //     textureCode: textureCode ?? oldTexture?.textureCode,
          //     texturePrice: texturePrice ?? oldTexture?.texturePrice,
          //     textureUrl: url ?? oldTexture?.textureUrl,

          //   });
          // }

          child.userData = {
            ...child.userData,
            ...(textureType === 'map'
              ? {
                  textureId,
                  material,
                  textureCode,
                  texturePrice,
                  textureUrl: url,
                }
              : {}),
          };
        }
      };

      group.children.forEach(group => {
        // kiểm tra phần tử này nếu là cửa thì thêm vào danh sách
        if (
          group.isGroup &&
          group.userData.type === InsideType.DOOR &&
          group.userData.isInside &&
          group.userData.materialType === 'nhom_kinh'
        ) {
          // this.doors.push(group);
          group?.traverse(child => {
            // if (!excludeMeshes.some(item => child.name.includes(item))) {
            setTexture(child);
            // }
          });
        }
      });

      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  /**
   * FIXED: Apply texture với exclude meshes và fix material sharing
   */
  async changeGlass({
    module,
    textureType = 'map',
    texture: { url, textureId, textureCode, texturePrice },
    color,
    options = {},
    includeMeshes = ['GLASS-PANEL'],
    excludeMeshes = [],
    excludeMeshTypes = [],
    includeMeshTypes = [],
  }) {
    const group = module.group;
    if (group?.userData?.moduleType?.includes('present')) return;

    try {
      let newTexture;

      if (textureType === 'color') {
        newTexture = new THREE.Color(color);
      } else {
        try {
          newTexture = await this.loadTexture(url);
          // this.applyTextureOptions(newTexture, {
          //   ...options,
          //   rotation: 0,
          //   repeat: [0.2, 0],
          // });
        } catch (error) {
          openMessageError({
            content: i18n.t('Failed to load materials. Please try again'),
          });
          return;
        }
      }

      group.traverse(child => {
        if (child.material) {
          const meshName = child.userData?.name || child.name || '';
          const type = child.userData.type ?? child?.parent?.userData?.type;
          // const type = child.userData.type;
          const cloneTexture = newTexture?.clone();

          ///// START
          // MODEL 1: Giữ nguyên scale hiện tại
          const modelWithScale = child.clone();
          modelWithScale.position.set(0, 0, 0);
          modelWithScale.rotation.set(0, 0, 0);
          modelWithScale.updateMatrix();
          modelWithScale.updateMatrixWorld(true);

          // MODEL 2: Reset scale về 1,1,1
          const modelOriginal = child.clone();
          modelOriginal.position.set(0, 0, 0);
          modelOriginal.rotation.set(0, 0, 0);
          modelOriginal.scale.set(1, 1, 1);
          modelOriginal.updateMatrix();
          modelOriginal.updateMatrixWorld(true);

          // Apply matrix vào geometry để có kích thước thực
          const geometryWithScale = modelWithScale.geometry.clone();
          geometryWithScale.applyMatrix4(modelWithScale.matrix);
          geometryWithScale.computeBoundingBox();

          const geometryOriginal = modelOriginal.geometry.clone();
          geometryOriginal.applyMatrix4(modelOriginal.matrix);
          geometryOriginal.computeBoundingBox();

          // Tính scale factor
          const scaleFactor = this.calculateScaleFactor(
            geometryWithScale,
            geometryOriginal
          );
          ///// END

          const shouldInclude =
            includeMeshes.some(
              includeName =>
                meshName.includes(includeName) || meshName === includeName
            ) || includeMeshTypes.includes(type);

          const shouldExclude =
            excludeMeshes.some(
              excludeName =>
                meshName.includes(excludeName) || meshName === excludeName
            ) ||
            this.FixedItem.some(item => item === type) ||
            excludeMeshTypes.includes(type);

          if (shouldInclude) {
            // Áp dụng UV với scale factor
            this.generateScaledUV(child.geometry, 0.999, scaleFactor);

            // this.ensureMaterialSupportsTexture(child.material);
            if (cloneTexture instanceof THREE.Texture) {
              this.applyTextureOptions(cloneTexture, {
                ...options,
                rotation: 0,
                repeat: [0.5, 0],
              });
            }
            if (textureType === 'color') {
              // Apply texture
              child.material.map = undefined;
            }
            child.material[textureType] = cloneTexture;
            child.material['color'] = null;
            child.material.name = url;
            child.material.needsUpdate = true;

            let oldTexture;
            if (shouldInclude) {
              let key = includeMeshes.find(
                item => meshName.includes(item) || meshName === item
              );
              oldTexture = module.textures.get(key);
              module.textures.set(key, {
                textureId: textureId ?? oldTexture?.textureId,
                textureCode: textureCode ?? oldTexture?.textureCode,
                texturePrice: texturePrice ?? oldTexture?.texturePrice,
                textureUrl: url ?? oldTexture?.textureUrl,
              });
            }

            child.userData = {
              ...child.userData,
              ...(textureType === 'map'
                ? {
                    textureId,
                    textureCode,
                    texturePrice,
                    textureUrl: url,
                  }
                : {}),
              ...(textureType === 'color'
                ? {
                    paintColor: color,
                  }
                : {}),
            };

            const parent = child.parent;

            if ([11].includes(parent.userData.type)) {
              parent.userData = {
                ...parent.userData,
                ...(textureType === 'map'
                  ? {
                      textureId,
                      textureCode,
                      texturePrice,
                      textureUrl: url,
                    }
                  : {}),
              };
            }
          }
          // if (
          //   includeMeshes.length === 0 &&
          //   includeMeshTypes.length === 0 &&
          //   ["HAU"].some((item) => meshName.includes(item)) &&
          //   !includeMeshes?.length
          // ) {
          //   this.ensureMaterialSupportsTexture(child.material);

          //   // Apply texture
          //   child.material[textureType] = newTexture;
          //   child.material.name = url;
          //   child.material.needsUpdate = true;

          //   child.userData = {
          //     ...child.userData,
          //     ...(textureType === "map"
          //       ? {
          //           textureId,
          //           supplier,
          //           material,
          //           textureCode,
          //           texturePrice,
          //           textureUrl: url,
          //         }
          //       : {}),
          //   };
          // } else {
          //   skippedCount++;
          //   return;
          // }
        }
      });
      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  /**
   */
  async changeShoeLowerBench({
    module,
    textureType = 'map',
    texture: { url, textureId, textureCode, texturePrice, supplier },
    options = {},
    includeMeshes = ['NEM-NGOI'],
    excludeMeshes = [],
    excludeMeshTypes = [],
    includeMeshTypes = [],
  }) {
    const group = module.group;
    if (group?.userData?.moduleType?.includes('present')) return;

    try {
      let newTexture;

      try {
        newTexture = await this.loadTexture(url);
        // this.applyTextureOptions(newTexture, {
        //   ...options,
        //   rotation: 0,
        //   repeat: [0.2, 0],
        // });
      } catch (error) {
        openMessageError({
          content: i18n.t('Failed to load materials. Please try again'),
        });
        return;
      }

      group.traverse(child => {
        if (child.material) {
          const meshName = child.userData?.name || child.name || '';
          const type = child.userData.type ?? child?.parent?.userData?.type;
          // const type = child.userData.type;
          const cloneTexture = newTexture?.clone();

          const shouldInclude =
            includeMeshes.some(
              includeName =>
                meshName.includes(includeName) || meshName === includeName
            ) || includeMeshTypes.includes(type);

          if (shouldInclude) {
            // MODEL 1: Giữ nguyên scale hiện tại
            const modelWithScale = child.clone();
            modelWithScale.position.set(0, 0, 0);
            modelWithScale.rotation.set(0, 0, 0);
            modelWithScale.updateMatrix();
            modelWithScale.updateMatrixWorld(true);

            // MODEL 2: Reset scale về 1,1,1
            const modelOriginal = child.clone();
            modelOriginal.position.set(0, 0, 0);
            modelOriginal.rotation.set(0, 0, 0);
            modelOriginal.scale.set(1, 1, 1);
            modelOriginal.updateMatrix();
            modelOriginal.updateMatrixWorld(true);

            // Apply matrix vào geometry để có kích thước thực
            const geometryWithScale = modelWithScale.geometry.clone();
            geometryWithScale.applyMatrix4(modelWithScale.matrix);
            geometryWithScale.computeBoundingBox();

            const geometryOriginal = modelOriginal.geometry.clone();
            geometryOriginal.applyMatrix4(modelOriginal.matrix);
            geometryOriginal.computeBoundingBox();

            // Tính scale factor
            const scaleFactor = this.calculateScaleFactor(
              geometryWithScale,
              geometryOriginal
            );
            // this.ensureMaterialSupportsTexture(child.material);
            if (cloneTexture instanceof THREE.Texture) {
              this.applyTextureOptions(cloneTexture, {
                ...options,
                rotation: Math.PI / 2,
                repeat: [scaleFactor.scaleX * 10, scaleFactor.scaleY * 10],
                wrapS: THREE.MirroredRepeatWrapping,
                wrapT: THREE.MirroredRepeatWrapping,
              });
            }
            if (!child.material.color) {
              child.material.color = new THREE.Color();
            }
            child.material = child.material.clone();
            child.material[textureType] = cloneTexture;
            child.material['color'] = null;
            child.material.name = url;
            child.material.needsUpdate = true;

            let oldTexture;
            if (shouldInclude) {
              let key = 'shoe_lower_bench';
              oldTexture = module.textures.get(key);
              module.textures.set(key, {
                textureId: textureId ?? oldTexture?.textureId,
                textureCode: textureCode ?? oldTexture?.textureCode,
                texturePrice: texturePrice ?? oldTexture?.texturePrice,
                textureUrl: url ?? oldTexture?.textureUrl,
                supplier: supplier,
              });
            }

            child.userData = {
              ...child.userData,
              ...(textureType === 'map'
                ? {
                    textureId,
                    textureCode,
                    texturePrice,
                    textureUrl: url,
                    supplier,
                  }
                : {}),
              ...(textureType === 'color'
                ? {
                    paintColor: color,
                  }
                : {}),
            };

            const parent = child.parent;

            if ([11].includes(parent.userData.type)) {
              parent.userData = {
                ...parent.userData,
                ...(textureType === 'map'
                  ? {
                      textureId,
                      textureCode,
                      texturePrice,
                      textureUrl: url,
                      supplier,
                    }
                  : {}),
              };
            }
          }
        }
      });
      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Đổi texture Hậu
   */
  async changeTextureHau({
    module,
    textureType = 'map',
    texture: {
      url,
      textureId,
      material,
      supplier,
      textureCode,
      texturePrice,
      no_grain = false,
      special = false,
    },
    color,
    options = {},
  }) {
    const group = module.group;
    if (group?.userData?.moduleType?.includes('present')) return;

    try {
      let newTexture;

      if (textureType === 'color') {
        newTexture = new THREE.Color(color);
      } else {
        try {
          newTexture = await this.loadTexture(url);
          if (newTexture instanceof THREE.Texture) {
            this.applyTextureOptions(newTexture, options);
          }
        } catch (error) {
          openMessageError({
            content: i18n.t('Failed to load materials. Please try again'),
          });
          return;
        }
      }

      // 🔧 BƯỚC 1: Clone material cho excluded meshes để tránh sharing
      // await this.isolateExcludedMeshMaterials(group, [...excludeMeshes, "HAU"]);

      group.traverse(child => {
        if (child.material) {
          const cloneTexture = newTexture?.clone();
          const meshName = child.name || child.userData?.name || '';

          ///// START
          // MODEL 1: Giữ nguyên scale hiện tại
          const modelWithScale = child.clone();
          modelWithScale.position.set(0, 0, 0);
          modelWithScale.rotation.set(0, 0, 0);
          modelWithScale.updateMatrix();
          modelWithScale.updateMatrixWorld(true);

          // MODEL 2: Reset scale về 1,1,1
          const modelOriginal = child.clone();
          modelOriginal.position.set(0, 0, 0);
          modelOriginal.rotation.set(0, 0, 0);
          modelOriginal.scale.set(1, 1, 1);
          modelOriginal.updateMatrix();
          modelOriginal.updateMatrixWorld(true);

          // Apply matrix vào geometry để có kích thước thực
          const geometryWithScale = modelWithScale.geometry.clone();
          geometryWithScale.applyMatrix4(modelWithScale.matrix);
          geometryWithScale.computeBoundingBox();

          const geometryOriginal = modelOriginal.geometry.clone();
          geometryOriginal.applyMatrix4(modelOriginal.matrix);
          geometryOriginal.computeBoundingBox();

          // Tính scale factor
          const scaleFactor = this.calculateScaleFactor(
            geometryWithScale,
            geometryOriginal
          );
          ///// END

          if (meshName.includes('HAU')) {
            // Áp dụng UV với scale factor
            this.generateScaledUV(child.geometry, 0.999, scaleFactor);

            if (
              module.cabinetType.includes('tv') ||
              meshName.includes('HAU-NGANG')
            ) {
              cloneTexture.rotation = Math.PI;
            }
            // this.ensureMaterialSupportsTexture(child.material);

            // Apply texture
            child.material[textureType] = cloneTexture;
            child.material.name = url;
            child.material.needsUpdate = true;

            let oldTexture;

            oldTexture = module.textures.get('Back');
            module.textures.set('Back', {
              textureId: textureId ?? oldTexture?.textureId,
              supplier: supplier ?? oldTexture?.supplier,
              material: material ?? oldTexture?.material,
              textureCode: textureCode ?? oldTexture?.textureCode,
              texturePrice: texturePrice ?? oldTexture?.texturePrice,
              textureUrl: url ?? oldTexture?.textureUrl,
              paintColor: color ?? oldTexture?.paintColor,
              no_grain: no_grain,
              specialTexture: special,
            });

            child.userData = {
              ...child.userData,
              ...(textureType === 'map'
                ? {
                    textureId,
                    supplier,
                    material,
                    textureCode,
                    texturePrice,
                    textureUrl: url,
                    no_grain,
                    specialTexture: special,
                  }
                : {}),
              ...(textureType === 'color'
                ? {
                    paintColor: color,
                  }
                : {}),
            };
          }
        }
      });
      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  async changeTextureCountertopAndBacksplash({
    scene,
    textureType = 'map',
    texture: {
      url,
      textureId,
      supplier,
      textureCode,
      formularPrice,
      texturePrice,
    },
    color,
    options = {},
    includeMeshes = ['COUNTERTOP', 'BACKSPLASH', 'MAT-DA', 'VACH-BEP'],
    includeTypes = [ModuleType.COUNTERTOP, ModuleType.BACKSPLASH],
  }) {
    const group = scene;

    try {
      let newTexture;

      if (textureType === 'color') {
        newTexture = new THREE.Color(color);
      } else {
        try {
          newTexture = await this.loadTexture(url);
        } catch (error) {
          openMessageError({
            content: i18n.t('Failed to load materials. Please try again'),
          });
          return;
        }
      }

      if (!newTexture) {
        openMessageError({
          content: i18n.t('Failed to load materials. Please try again'),
        });
        return;
      }

      group.traverse(child => {
        const type = child.userData.type;

        if (
          child.material &&
          [ModuleType.COUNTERTOP, ModuleType.BACKSPLASH].includes(type)
        ) {
          const meshName = child.userData?.name || child.name || '';

          const shouldInclude =
            includeMeshes.some(
              includeName =>
                meshName.includes(includeName) || meshName === includeName
            ) || includeTypes.some(item => item === type);

          if (shouldInclude) {
            // this.ensureMaterialSupportsTexture(child.material);
            const cloneTexture = newTexture?.clone();

            if (textureType !== 'color' && cloneTexture.image) {
              const imageWidth = cloneTexture.image.width;
              const imageHeight = cloneTexture.image.height;

              const bbox = new THREE.Box3().setFromObject(child);
              const size = new THREE.Vector3();
              bbox.getSize(size);

              let length;
              let repeatX = 1;
              let repeatY = 1;
              let rotation = 0; // Khai báo rotation
              let center = [0, 0];
              const flipY = true;
              const flipX = true;

              if ([ModuleType.COUNTERTOP].includes(type)) {
                const countertopDepth = child.userData.countertopDepth;
                if (countertopDepth !== undefined) {
                  const lengthX =
                    Math.abs(size.x - countertopDepth) >
                    Math.abs(size.z - countertopDepth)
                      ? size.x
                      : size.z;

                  repeatX = lengthX / 1.2;
                  const lengthY = countertopDepth;
                  repeatY = lengthY / 0.8;
                  if (meshName.includes('1')) {
                    rotation = Math.PI;
                    // center = [0, 1];
                  } else {
                    rotation = Math.PI / 2;
                    // center = [1, 0];
                  }
                } else {
                  const orientation =
                    child.userData.orientation ??
                    GeometryUtils.getModuleOrientation(child);

                  if (['ROTATED_270', 'ROTATED_90'].includes(orientation)) {
                    const lengthY = size.z;
                    repeatY = lengthY / 0.8;
                    const lengthX = size.x;
                    repeatX = lengthX / 1.2;
                    rotation = 0;
                    // center = [0, 1];
                  } else {
                    const lengthX = size.z;
                    repeatX = lengthX / 1.2;
                    const lengthY = size.x;
                    repeatY = lengthY / 0.8;
                    rotation = Math.PI / 2;
                    // center = [1, 0];
                  }
                }
              } else if ([ModuleType.BACKSPLASH].includes(type)) {
                // const sizeArray = [size.x, size.y, size.z];
                // sizeArray.sort((a, b) => b - a);

                const sizeArray = [
                  Math.max(size.x, size.z),
                  size.y,
                  Math.min(size.x, size.z),
                ];

                const orientation =
                  child.userData.orientation ??
                  GeometryUtils.getModuleOrientation(child);

                if (['ROTATED_270', 'ROTATED_90'].includes(orientation)) {
                  const lengthX = sizeArray[0];
                  repeatX = lengthX / 1.2;
                  const lengthY = sizeArray[1];
                  repeatY = lengthY / 0.8;
                  rotation = 0;
                  // center = [0.5, 0.5];
                } else {
                  const lengthX = sizeArray[0];
                  repeatX = lengthX / 1.2;
                  const lengthY = sizeArray[1];
                  repeatY = lengthY / 0.8;
                  rotation = 0;
                  // center = [0.5, 0.5];
                }
              }

              // Apply rotation SAU KHI set repeat
              if (cloneTexture instanceof THREE.Texture) {
                this.applyTextureOptions(cloneTexture, {
                  wrapS: THREE.RepeatWrapping,
                  wrapT: THREE.RepeatWrapping,
                  repeat: [repeatX, repeatY],
                  rotation,
                  flipY,
                  flipX,
                  center,
                });
              }
            }

            // Apply texture
            child.material[textureType] = cloneTexture;
            child.material.color = new THREE.Color();
            child.material.name = url;
            child.material.needsUpdate = true;

            child.userData = {
              ...child.userData,
              ...(textureType === 'map'
                ? {
                    textureId,
                    supplier,
                    textureCode,
                    texturePrice,
                    textureUrl: url,
                    formularPrice,
                  }
                : {}),
            };

            // if (includeTypes.includes(ModuleType.COUNTERTOP)) {
            //   module.countertop = {
            //     textureId,
            //     supplier,
            //     textureCode,
            //     texturePrice,
            //     textureUrl: url,
            //     formularPrice,
            //   };
            // } else if (includeTypes.includes(ModuleType.BACKSPLASH)) {
            //   module.backsplash = {
            //     textureId,
            //     supplier,
            //     textureCode,
            //     texturePrice,
            //     textureUrl: url,
            //     formularPrice,
            //   };
            // }
          }
        }
      });
      return newTexture;
    } catch (error) {
      throw error;
    }
  }

  // /**
  //  * 🔧 NEW: Tách riêng material cho excluded meshes để tránh sharing
  //  */
  // async isolateExcludedMeshMaterials(module, excludeMeshes) {
  //   // const moduleData = this.modules.get(moduleId);
  //   // if (!moduleData) return;

  //   // Tìm tất cả material được share
  //   const sharedMaterials = new Map(); // materialUUID -> [data objects]

  //   this.extractMaterials(module).forEach(data => {
  //     const materialUUID = data.material.uuid;
  //     if (!sharedMaterials.has(materialUUID)) {
  //       sharedMaterials.set(materialUUID, []);
  //     }
  //     sharedMaterials.get(materialUUID).push(data);
  //   });

  //   // Clone material cho excluded meshes nếu material được share
  //   sharedMaterials.forEach((dataList, materialUUID) => {
  //     if (dataList.length <= 1) return; // Không share thì skip

  //     const excludedData = [];
  //     const includedData = [];

  //     dataList.forEach(data => {
  //       const meshName =
  //         data.meshType || data.child.userData?.name || data.child.name || '';
  //       const type = data.child.userData?.type;

  //       const shouldExclude =
  //         excludeMeshes.some(
  //           excludeName =>
  //             meshName.includes(excludeName) || meshName === excludeName
  //         ) || this.FixedItem.some(item => type === item);

  //       if (shouldExclude) {
  //         excludedData.push(data);
  //       } else {
  //         includedData.push(data);
  //       }
  //     });

  //     // Nếu có cả excluded và included mesh dùng chung material
  //     if (excludedData.length > 0 && includedData.length > 0) {
  //       // Clone material cho excluded meshes
  //       excludedData.forEach(data => {
  //         const clonedMaterial = data.material?.clone();
  //         data.child.material = clonedMaterial;

  //         // Update material reference trong moduleData
  //         data.material = clonedMaterial;
  //       });
  //     }
  //   });
  // }

  clearTextureCache(url = null) {
    if (url) {
      const texture = this.textureCache.get(url);
      if (texture) {
        if (typeof texture.dispose === 'function') {
          texture.dispose();
        }
        this.textureCache.delete(url);
      }
    } else {
      this.textureCache.forEach(texture => {
        if (typeof texture?.dispose === 'function') {
          texture.dispose();
        }
      });
      this.textureCache.clear();
    }
  }

  clearModules(id = null) {
    if (id) {
      this.modules.delete(id);
    } else {
      this.modules.clear();
    }
  }

  /**
   * Cấu hình texture với các tùy chọn tối ưu
   */
  applyTextureOptions(texture, options) {
    const {
      wrapS = THREE.MirroredRepeatWrapping,
      wrapT = THREE.MirroredRepeatWrapping,
      center = [0.5, 0.5],
      repeat = [1, 1],
      offset = [0, 0],
      rotation = Math.PI / 2,
      flipY = false,
      flipX = false,
      generateMipmaps = true,
      minFilter = THREE.LinearMipmapLinearFilter,
      magFilter = THREE.LinearFilter,
      anisotropy = 1600,
    } = options;
    texture.wrapS = wrapS;
    texture.wrapT = wrapT;
    texture.center.set(center[0], center[1]);
    texture.repeat.set(repeat[0], repeat[1]);
    texture.offset.set(offset[0], offset[1]);
    texture.rotation = rotation;
    texture.flipY = flipY;
    texture.flipX = flipX;
    texture.generateMipmaps = generateMipmaps;
    texture.minFilter = minFilter;
    texture.magFilter = magFilter;
    texture.anisotropy = anisotropy;
    texture.matrixAutoUpdate = true;
    // texture.needsUpdate = true;
  }

  // /**
  //  * Kiểm tra và sửa UV mapping
  //  */
  // checkAndFixUVMapping(mesh, materialKey) {
  //   if (!mesh.geometry) return;

  //   const geometry = mesh.geometry;

  //   if (!geometry.attributes.uv) {
  //     if (
  //       geometry.type === 'BoxGeometry' ||
  //       geometry.type === 'BufferGeometry'
  //     ) {
  //       this.generateBasicUV(geometry);
  //     }
  //   } else {
  //     const uvArray = geometry.attributes.uv.array;
  //     let hasValidUV = false;

  //     for (let i = 0; i < uvArray.length; i += 2) {
  //       if (uvArray[i] !== 0 || uvArray[i + 1] !== 0) {
  //         hasValidUV = true;
  //         break;
  //       }
  //     }

  //     if (!hasValidUV) {
  //       this.generateBasicUV(geometry);
  //     }
  //   }
  // }

  // /**
  //  * Tạo UV mapping cơ bản cho geometry
  //  */
  // generateBasicUV(geometry) {
  //   geometry.computeBoundingBox();
  //   const { min, max } = geometry.boundingBox;
  //   const range = new THREE.Vector3().subVectors(max, min);
  //   const positions = geometry.attributes.position.array;
  //   const uvs = [];
  //   for (let i = 0; i < positions.length; i += 3) {
  //     const x = (positions[i] - min.x) / range.x;
  //     const y = (positions[i + 1] - min.y) / range.y;
  //     const z = (positions[i + 2] - min.z) / range.z;
  //     let u, v;

  //     u = x;
  //     v = y;

  //     uvs.push(u, v);
  //   }
  //   geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  //   geometry.attributes.uv.needsUpdate = true;
  // }

  // /**
  //  * Đảm bảo material hỗ trợ texture
  //  */
  // ensureMaterialSupportsTexture(material) {
  //   if (
  //     material.type === 'MeshStandardMaterial' ||
  //     material.type === 'MeshPhysicalMaterial'
  //   ) {
  //     if (material.roughness >= 1) {
  //       material.roughness = 0.7;
  //     }
  //   }
  // }

  // hasValidUV(mesh) {
  //   if (!mesh.geometry || !mesh.geometry.attributes.uv) return false;

  //   const uvArray = mesh.geometry.attributes.uv.array;

  //   for (let i = 0; i < uvArray.length; i += 2) {
  //     if (uvArray[i] !== 0 || uvArray[i + 1] !== 0) {
  //       return true;
  //     }
  //   }

  //   return false;
  // }

  // /**
  //  * NEW: Kiểm tra xem có material nào được share giữa các mesh không
  //  */
  // checkMaterialSharing(module) {
  //   // const moduleData = this.modules.get(moduleId);
  //   // if (!moduleData) return;

  //   const materialGroups = new Map();

  //   this.extractMaterials(module).forEach((data, materialKey) => {
  //     const materialUUID = data.material.uuid;
  //     const meshName =
  //       data.meshType ||
  //       data.child.userData?.name ||
  //       data.child.name ||
  //       'Unknown';

  //     if (!materialGroups.has(materialUUID)) {
  //       materialGroups.set(materialUUID, []);
  //     }
  //     materialGroups.get(materialUUID).push(meshName);
  //   });

  //   return materialGroups;
  // }

  // /**
  //  * 🔧 NEW: Force isolate material cho specific mesh
  //  */
  // isolateSpecificMeshMaterial(module, meshName) {
  //   // const moduleData = this.modules.get(moduleId);
  //   // if (!moduleData) {
  //   //   console.error(`Module ${moduleId} not found`);
  //   //   return;
  //   // }

  //   let isolated = false;

  //   this.extractMaterials(module).forEach((data, materialKey) => {
  //     const currentMeshName =
  //       data.meshType || data.child.userData?.name || data.child.name || '';

  //     if (currentMeshName === meshName || currentMeshName.includes(meshName)) {
  //       const clonedMaterial = data.material?.clone();
  //       data.child.material = clonedMaterial;
  //       data.material = clonedMaterial;

  //       isolated = true;
  //     }
  //   });

  //   if (!isolated) {
  //     console.warn(`⚠️ Mesh "${meshName}" not found for isolation`);
  //   }

  //   return isolated;
  // }

  clearTextile(module) {
    const group = module.group;
    group.traverse(child => {
      if (child.material && child.name === 'NEM-NGOI') {
        const textureId = child.userData?.textureId;
        if (textureId) {
          child.material['map'] = null;
          child.material['color'] = null;
          child.material.name = null;
          child.material.needsUpdate = true;

          module.textures.set('shoe_lower_bench', {});

          child.userData = {
            ...child.userData,
            ...{
              textureId: null,
              supplier: null,
              material: null,
              textureCode: null,
              texturePrice: null,
              textureUrl: null,
            },
            ...{
              paintColor: null,
            },
          };

          const parent = child.parent;

          if ([11].includes(parent.userData.type)) {
            parent.userData = {
              ...parent.userData,
              ...{
                textureId: null,
                supplier: null,
                material: null,
                textureCode: null,
                texturePrice: null,
                textureUrl: null,
              },
            };
          }
        }
      }
    });
  }

  clearTexture(module) {
    const group = module.group;
    group.traverse(child => {
      if (
        child.material &&
        !['stone', 'glass', 'textile'].some(
          type => child.userData.supplier?.materialType === type
        )
      ) {
        const textureId = child.userData?.textureId;
        if (textureId) {
          child.material['map'] = null;
          child.material['color'] = null;
          child.material.name = null;
          child.material.needsUpdate = true;

          module.textures.set('Back', {});
          module.textures.set('frame', {});
          module.textures.set('DOOR', {});

          child.userData = {
            ...child.userData,
            ...{
              textureId: null,
              supplier: null,
              material: null,
              textureCode: null,
              texturePrice: null,
              textureUrl: null,
            },
            ...{
              paintColor: null,
            },
          };

          const parent = child.parent;

          if ([11].includes(parent.userData.type)) {
            parent.userData = {
              ...parent.userData,
              ...{
                textureId: null,
                supplier: null,
                material: null,
                textureCode: null,
                texturePrice: null,
                textureUrl: null,
              },
            };
          }
        }
      }
    });
  }

  dispose() {
    this.clearTextureCache();
    this.clearModules();
  }
}

/* ===== USAGE EXAMPLES ===== */

// 1. Cách sử dụng cơ bản
/*
const textureController = new TextureModuleController();
textureController.registerModule('cabinet_module', moduleObject);

// Debug trước khi apply
textureController.debugModule('cabinet_module');

// Apply texture với exclude HAU (default)
await textureController.changeTexture('cabinet_module', 'map', '/textures/wood.jpg');

// Hoặc exclude nhiều mesh
await textureController.changeTexture(
    'cabinet_module', 
    'map', 
    '/textures/wood.jpg',
    { repeat: [1, 1] },
    ['HAU', 'XUONG-SAU'] // Exclude multiple meshes
);
*/
